import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { StatItem, StatTable } from 'components/StatTable';
import { useWaterLevelsChart } from 'features/waterLevel/hooks/useWaterLevelsChart';
import {
  WaterLevel,
  WaterLevelObservation,
} from 'features/waterLevel/waterLevel.types';
import { TFunction } from 'i18next';
import { EchartsWrapper } from 'libs/echarts/EchartsWrapper';
import { getFloodCategoryFromLevel } from 'features/waterLevel/waterLevel.utils';
import { useTranslation } from 'react-i18next';
import { formatNumber } from 'shared/utils';
import { makeStyles } from 'tss-react/mui';

type EmbeddedWaterLevelProps = {
  waterLevel: WaterLevel;
  observations: WaterLevelObservation[];
};

const EMBED_WIDTH = 700;
const EMBED_HEADERBAR_HEIGHT = 64; // MUI standard header size
const EMBED_HEIGHT = 368 - EMBED_HEADERBAR_HEIGHT;

const getFloodLevelTranslated = (
  t: TFunction,
  category: string | null,
): string | null => {
  if (category && ['major', 'minor', 'moderate', 'action'].includes(category)) {
    return t(`waterLevel.levels.${category}`);
  }

  return category;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  card: {
    borderRadius: 0,
    minHeight: '100%',
    width: '100%',
    zIndex: theme.zIndex.drawer - 1,
    overflow: 'auto',
    maxHeight: '100%',
    paddingTop: theme.spacing(2),
  },
}));

export const EmbeddedWaterLevel = (
  props: EmbeddedWaterLevelProps,
): JSX.Element => {
  const { waterLevel, observations } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const waterLevelsChartOptions = useWaterLevelsChart({
    observations: observations || [],
    waterLevel,
  });

  const { level: latestLevel = undefined, flow: latestFlow = undefined } =
    observations.length ? observations[0] : {};

  const latestCategory = latestLevel
    ? getFloodCategoryFromLevel(latestLevel, waterLevel.floodCategories)
    : null;

  const stats: StatItem[] = [
    {
      label: t('waterLevel.height'),
      value: latestLevel
        ? `${formatNumber(latestLevel, 1)} ${waterLevel.units.level}`
        : null,
    },
    {
      label: t('waterLevel.flow'),
      value: latestFlow
        ? `${formatNumber(latestFlow)} ${waterLevel.units.flow}`
        : null,
      tooltipText:
        latestFlow && waterLevel.units.flow === 'kcfs'
          ? t('waterLevel.kcfs')
          : undefined,
    },
    {
      label: t('waterLevel.floodLevel'),
      valueTypographyProps: { textTransform: 'capitalize' },
      value: getFloodLevelTranslated(t, latestCategory) || null,
    },
  ];

  return (
    <Box className={classes.container}>
      <Box
        sx={{
          flexShrink: 1,
          flexBasis: `${EMBED_WIDTH + 220 / 2}px`,
          minWidth: '376px',
          height: EMBED_HEIGHT,
          px: 2,
        }}
      >
        {waterLevelsChartOptions && (
          <EchartsWrapper option={waterLevelsChartOptions} />
        )}
      </Box>

      <Box sx={{ width: '100%', maxWidth: '532px' }}>
        <Card className={classes.card}>
          <CardContent sx={{ py: 0 }}>
            <Stack spacing={2}>
              <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                {waterLevel.name}
              </Typography>

              <StatTable stats={stats} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
